/**
 * Interactive loan-tape preview. Renders the first-rows table; clicking a
 * column header logs that column's lineage — the raw source tab/column, the
 * canonical field it maps to, and the SQL transform in English — as a step in
 * the Audit Log (via ``onInspectColumn``). Every click appends; the audit log
 * keeps the full series so multiple columns can be inspected in sequence.
 */

import React, { useState } from "react";
import type { LoanTapePreviewColumn } from "./Chat/Chat.types";

interface LoanTapePreviewBlockViewProps {
	columns: LoanTapePreviewColumn[];
	rows: string[][];
	/**
	 * Called on every column-header click. The parent appends it to the Audit
	 * Log as an accumulating series (each click is a new step, not a replace).
	 */
	onInspectColumn?: (column: LoanTapePreviewColumn) => void;
}

const LoanTapePreviewBlockView: React.FC<LoanTapePreviewBlockViewProps> = ({
	columns,
	rows,
	onInspectColumn,
}) => {
	// Tracks the most recently inspected column purely for header highlighting;
	// the lineage detail itself now lives in the Audit Log series.
	const [selected, setSelected] = useState<number | null>(null);

	if (columns.length === 0) return null;

	return (
		<div className="my-3">
			<div className="rounded-lg border border-gray-200 overflow-hidden">
				<div className="overflow-x-auto">
					<table className="w-full border-collapse text-sm">
						<thead>
							<tr className="bg-gray-50">
								{columns.map((c, i) => (
									<th
										key={i}
										onClick={() => {
											setSelected(i);
											onInspectColumn?.(c);
										}}
										title="Click to trace this column's lineage"
										className={`px-3 py-2 font-medium text-gray-700 whitespace-nowrap cursor-pointer border-b border-gray-200 hover:bg-[#004C4B14] ${
											c.align === "right" ? "text-right" : "text-left"
										} ${selected === i ? "bg-[#004C4B1F]" : ""}`}
									>
										<span className="border-b border-dotted border-gray-400">
											{c.header}
										</span>
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map((row, ri) => (
								<tr key={ri} className="even:bg-gray-50/50">
									{row.map((cell, ci) => (
										<td
											key={ci}
											className={`px-3 py-1.5 whitespace-nowrap text-gray-800 ${
												columns[ci]?.align === "right"
													? "text-right tabular-nums"
													: "text-left"
											} ${selected === ci ? "bg-[#004C4B0A]" : ""}`}
										>
											{cell}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default LoanTapePreviewBlockView;
